import { AlertTriangle, MessageSquare, Star, User } from 'lucide-react';

import { type GetAllReservations } from '@virtual-care/client';
import { displayName } from '@virtual-care/isomorphic';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from '@virtual-care/shadcn';

type ReservationRecord = GetAllReservations['records'][number];

interface RatingBadgeProps {
  summary: NonNullable<ReservationRecord['ratingSummary']>;
}

export function RatingBadge({ summary }: RatingBadgeProps) {
  const { overallAverage, isLowRating, hasReportedIssue } = summary;

  return (
    <div className="flex items-center gap-1">
      <Star
        className={cn(
          'size-4',
          isLowRating
            ? 'fill-red-500 text-red-500'
            : 'fill-yellow-500 text-yellow-500',
        )}
      />
      <span
        className={cn(
          'text-sm font-medium',
          isLowRating ? 'text-red-600 dark:text-red-400' : 'text-foreground',
        )}
      >
        {overallAverage.toFixed(1)}
      </span>
      {hasReportedIssue && <AlertTriangle className="size-3.5 text-red-600" />}
    </div>
  );
}

interface RatingsDialogProps {
  selectedReservation: ReservationRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RatingsDialog({
  selectedReservation,
  open,
  onOpenChange,
}: RatingsDialogProps) {
  if (!selectedReservation?.ratingSummary) return null;

  const { patientReview, doctorReview, isLowRating, hasReportedIssue } =
    selectedReservation.ratingSummary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Appointment Reviews - Case #{selectedReservation.reservationNumber}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {(isLowRating || hasReportedIssue) && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/10">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 size-5 flex-shrink-0 text-red-600" />
                <div>
                  <p className="font-semibold text-red-800 dark:text-red-400">
                    {hasReportedIssue ? 'Reported Issue' : 'Low Rating'}
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-500">
                    {hasReportedIssue
                      ? 'This appointment has low ratings with detailed feedback requiring follow-up.'
                      : 'This appointment received a low average rating.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            {patientReview && (
              <ReviewCard
                title="Patient Review"
                review={patientReview}
                userName={displayName(selectedReservation.user)}
              />
            )}

            {doctorReview && selectedReservation.doctor && (
              <ReviewCard
                title="Doctor Review"
                review={doctorReview}
                userName={`${selectedReservation.doctor.firstName} ${selectedReservation.doctor.lastName}`}
              />
            )}

            {!patientReview && !doctorReview && (
              <div className="text-muted-foreground col-span-2 py-8 text-center">
                <MessageSquare className="mx-auto mb-3 h-12 w-12 opacity-50" />
                <p>No reviews submitted yet</p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ReviewCardProps {
  title: string;
  review: {
    averageRating: number;
    textFeedback: string | null;
    categoryRatings: Array<{ categoryName: string; rating: number }>;
  };
  userName: string;
}

function ReviewCard({ title, review, userName }: ReviewCardProps) {
  return (
    <div className="bg-muted/50 space-y-4 rounded-lg p-4">
      <div>
        <h3 className="mb-1 flex items-center gap-2 font-semibold">
          <User className="size-4" />
          {title}
        </h3>
        <p className="text-muted-foreground text-sm">{userName}</p>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Overall:</span>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={cn(
                'size-4',
                star <= Math.round(review.averageRating)
                  ? 'fill-yellow-500 text-yellow-500'
                  : 'text-gray-300 dark:text-gray-600',
              )}
            />
          ))}
          <span className="ms-1 text-sm font-medium">
            {review.averageRating.toFixed(1)}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">Category Ratings:</p>
        {review.categoryRatings.map((cat) => (
          <div
            key={cat.categoryName}
            className="flex items-center justify-between text-sm"
          >
            <span className="text-muted-foreground">{cat.categoryName}</span>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={cn(
                    'size-3',
                    star <= cat.rating
                      ? 'fill-yellow-500 text-yellow-500'
                      : 'text-gray-300 dark:text-gray-600',
                  )}
                />
              ))}
              <span className="ms-1 text-xs font-medium">{cat.rating}</span>
            </div>
          </div>
        ))}
      </div>

      {review.textFeedback && (
        <div>
          <p className="mb-1 text-sm font-medium">Feedback:</p>
          <p className="text-muted-foreground border-muted border-s-2 ps-3 text-sm whitespace-pre-wrap">
            {review.textFeedback}
          </p>
        </div>
      )}
    </div>
  );
}
